import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

import {
  GET_WORKSPACE_INVITE_BY_TOKEN_QUERY,
  ACCEPT_WORKSPACE_INVITE_MUTATION,
  DECLINE_WORKSPACE_INVITE_MUTATION,
} from '../services/graphql';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import Logo from '../components/common/Logo';

export default function WorkspaceInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { refetchWorkspaces, switchWorkspace } = useWorkspace();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Fetch invitation details
  const { data, loading, error } = useQuery(GET_WORKSPACE_INVITE_BY_TOKEN_QUERY, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const [acceptInvite] = useMutation(ACCEPT_WORKSPACE_INVITE_MUTATION);
  const [declineInvite] = useMutation(DECLINE_WORKSPACE_INVITE_MUTATION);

  const invitation = data?.getWorkspaceInviteByToken;

  // Redirect to signup/login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated && invitation) {
      // Pass the invite token directly so signup can prefill and lock the email
      navigate(`/signup?inviteToken=${token}`);
    }
  }, [loading, isAuthenticated, invitation, navigate, token]);

  const handleAccept = async () => {
    setActionLoading(true);
    setActionResult(null);

    try {
      const { data: result } = await acceptInvite({
        variables: { token },
      });

      if (result?.acceptWorkspaceInvite?.success) {
        setActionResult({
          success: true,
          message: 'Successfully joined the workspace!',
        });

        // Refresh workspaces and switch to the new one
        await refetchWorkspaces();
        if (result.acceptWorkspaceInvite.workspace?.id) {
          switchWorkspace(result.acceptWorkspaceInvite.workspace.id);
        }

        // Redirect to vault after a short delay
        setTimeout(() => {
          navigate('/vault');
        }, 1500);
      } else {
        setActionResult({
          success: false,
          message: result?.acceptWorkspaceInvite?.message || 'Failed to accept invitation',
        });
      }
    } catch (err) {
      setActionResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to accept invitation',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    setActionResult(null);

    try {
      const { data: result } = await declineInvite({
        variables: { token },
      });

      if (result?.declineWorkspaceInvite?.success) {
        setActionResult({
          success: true,
          message: 'Invitation declined.',
        });

        // Redirect to vault
        setTimeout(() => {
          navigate('/vault');
        }, 1500);
      } else {
        setActionResult({
          success: false,
          message: result?.declineWorkspaceInvite?.message || 'Failed to decline invitation',
        });
      }
    } catch (err) {
      setActionResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to decline invitation',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Invalid or expired invitation
  if (!invitation || error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.default',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.light',
                color: 'error.main',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon fontSize="large" />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Invalid or Expired Invitation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This invitation link is no longer valid. It may have expired or already been used.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/login">
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Email mismatch check
  const emailMismatch =
    isAuthenticated && user && user.email.toLowerCase() !== invitation.invitedEmail.toLowerCase();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Logo size="large" />
            </Box>
          </Box>

          {actionResult && (
            <Alert
              severity={actionResult.success ? 'success' : 'error'}
              icon={actionResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
              sx={{ mb: 3 }}
            >
              {actionResult.message}
            </Alert>
          )}

          {emailMismatch && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This invitation was sent to <strong>{invitation.invitedEmail}</strong>, but you're
              signed in as <strong>{user?.email}</strong>. Please sign in with the correct account.
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.light',
                color: 'primary.main',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You're Invited!
            </Typography>
            <Typography color="text.secondary">
              <strong>{invitation.invitedByEmail}</strong> has invited you to join
            </Typography>
            <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
              {invitation.workspaceName}
            </Typography>
          </Box>

          {!emailMismatch && !actionResult?.success && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={handleDecline}
                disabled={actionLoading}
              >
                Decline
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAccept}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Accept Invitation'}
              </Button>
            </Box>
          )}

          {emailMismatch && (
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to={`/login?redirect=/workspace-invite/${token}&inviteToken=${token}`}>
                Sign in with a different account
              </Link>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              By joining this workspace, you'll be able to share passwords and items securely with
              other members.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

